// EditCompanyV2.tsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Box, CircularProgress } from "@mui/material";
import AddCompanyLayout from "./AddCompanyLayout";
import {
  COMPANY_STEPS,
  CompanyFormData,
  INITIAL_FORM_DATA,
  BranchData,
  RepresentativeData,
} from "../types/company-types";
import Identity from "./steps/Identity";
import Branches from "./steps/Branches";
import Representatives from "./steps/Representatives";
import Branding from "./steps/Branding";
import apiService from "../../../service/Api";
import { normalizeUrl } from "../../../utils/normalizeUrl";

// ── API response shapes ──────────────────────────────────────────────────

interface CompanyResponse {
  id: number;
  name: string;
  phone_number?: string;
  primary_email?: string;
  street_address?: string;
  city?: string;
  company_tags?: string[];
  company_type?: string;
  company_logo?: string | null;
  linkedin_profile?: string | null;
  facebook_profile?: string | null;
  instagram_profile?: string | null;
  x_profile?: string | null;
  website?: string | null;
}

interface BranchResponse {
  id: number;
  name: string;

  phone_number?: string;
  email?: string;
  city?: string;
  street_address?: string;
}

interface ContactResponse {
  id: number;
  full_name: string;
  email: string;
  role: string | null;
  phone: string;
}

// ── Transformers ──────────────────────────────────────────────────────────

function transformBranchFromApi(b: BranchResponse): BranchData {
  return {
    id: String(b.id),
    name: b.name || "",

    phone: b.phone_number || "",
    email: b.email || "",
    city: b.city || "",
    address: b.street_address || "",
  };
}

function transformContactFromApi(c: ContactResponse): RepresentativeData {
  const [firstName, ...rest] = (c.full_name || "").split(" ");
  return {
    id: String(c.id),
    firstName: firstName || "",
    lastName: rest.join(" "),
    role: c.role || "",
    phone: c.phone || "",
    email: c.email || "",
    existingContactId: c.id,
  };
}

export default function EditCompanyV2() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<CompanyFormData>(INITIAL_FORM_DATA);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // snapshots to diff against on submit
  const originalBranchIds = useRef<Set<string>>(new Set());
  const originalRepContactIds = useRef<Set<number>>(new Set());
  const hqBranchId = useRef<number | null>(null);

  // ── Fetch existing company + branches + representatives ────────────────
  const fetchAll = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const companyRes = await apiService.get<CompanyResponse>(
        `api/companies/company/${id}/`,
      );
      const company = companyRes.data;

      const branchRes = await apiService.get<{ results: BranchResponse[] }>(
        `api/companies/company-branch/?company=${id}`,
      );
      const rawBranches = branchRes.data.results ?? [];
      const branches = rawBranches.map(transformBranchFromApi);
      originalBranchIds.current = new Set(branches.map((b) => b.id));

      const hq = rawBranches.find((b) => b.name === "HQ");
      hqBranchId.current = hq?.id ?? rawBranches[0]?.id ?? null;

      const contactRes = await apiService.get<{ results: ContactResponse[] }>(
        `api/contacts/contact/by-company/?company_id=${id}`,
      );
      const rawContacts = contactRes.data.results ?? [];
      const representatives = rawContacts.map(transformContactFromApi);
      originalRepContactIds.current = new Set(
        representatives
          .map((r) => r.existingContactId)
          .filter((v): v is number => v !== undefined),
      );

      setFormData({
        ...INITIAL_FORM_DATA,
        name: company.name || "",

        industry: company.company_type || "",
        phone: company.phone_number || "",
        primaryEmail: company.primary_email || "",
        website: company.website || "",
        address: company.street_address || "",
        city: company.city || "", // create flow never actually sends this field — left blank to match
        branches,
        representatives,
        logoMode: company.company_logo ? "url" : "initials",
        logoUrl: company.company_logo || "",
        logoFile: null,
        linkedin: company.linkedin_profile || "",
        facebook: company.facebook_profile || "",
        instagram: company.instagram_profile || "",
        x: company.x_profile || "",
        websiteBranding: "",
        tags: company.company_tags || [],
      });
    } catch (err) {
      console.error("Failed to fetch company:", err);
      toast.error("Failed to load company details.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ── Validation (identical to create) ────────────────────────────────────
  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0:
        if (!formData.name.trim()) {
          toast.error("Company name is required.");
          return false;
        }
        if (!formData.phone.trim()) {
          toast.error("Phone number is required.");
          return false;
        }
        if (!formData.primaryEmail.trim()) {
          toast.error("Email is required.");
          return false;
        }
        return true;
      case 1: {
        const unnamedBranch = formData.branches.find((b) => !b.name.trim());
        if (unnamedBranch) {
          toast.error("All branches must have a name.");
          return false;
        }
        return true;
      }
      case 2: {
        const unnamedRep = formData.representatives.find(
          (r) => !r.firstName.trim(),
        );
        if (unnamedRep) {
          toast.error("All representatives must have a first name.");
          return false;
        }
        return true;
      }
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) return;
    setActiveStep((prev) => Math.min(prev + 1, COMPANY_STEPS.length - 1));
  };
  const handlePrevious = () => setActiveStep((prev) => Math.max(prev - 1, 0));
  const handleStepClick = (step: number) => {
    if (step < activeStep) setActiveStep(step);
  };

  const handleSubmit = async () => {
    console.log("handleSubmit called, activeStep:", activeStep);
    console.log("formData at submit:", formData);
    console.log(
      "Reps at submit:",
      formData.representatives.map((r) => ({
        name: r.firstName,
        existingContactId: r.existingContactId,
      })),
    );
    for (let i = 0; i < COMPANY_STEPS.length; i++) {
      const valid = validateStep(i);
      console.log(`Step ${i} valid:`, valid);
      if (!valid) {
        setActiveStep(i);
        return;
      }
    }
    if (!id) return;
    console.log("All steps valid, proceeding with PATCH...");

    setIsSubmitting(true);
    try {
      // REPLACE the company patch section in handleSubmit:
      const companyPayload = new FormData();
      companyPayload.append("name", formData.name);
      companyPayload.append("phone_number", formData.phone);
      companyPayload.append("primary_email", formData.primaryEmail);
      companyPayload.append("street_address", formData.address);
      companyPayload.append("city", formData.city);
      companyPayload.append("company_type", formData.industry);
      companyPayload.append("website", normalizeUrl(formData.website));
      companyPayload.append(
        "linkedin_profile",
        normalizeUrl(formData.linkedin),
      );
      companyPayload.append(
        "facebook_profile",
        normalizeUrl(formData.facebook),
      );
      companyPayload.append(
        "instagram_profile",
        normalizeUrl(formData.instagram),
      );
      companyPayload.append("x_profile", normalizeUrl(formData.x));
      formData.tags.forEach((tag) =>
        companyPayload.append("company_tags", tag),
      );
      if (formData.logoMode === "upload" && formData.logoFile) {
        companyPayload.append("company_logo", formData.logoFile);
      }

      await apiService.patch(`api/companies/company/${id}/`, companyPayload);

      // 2. Branches — diff by id membership in the original snapshot
      const currentBranchIds = new Set(formData.branches.map((b) => b.id));

      const branchOps: Promise<unknown>[] = [];

      formData.branches.forEach((b) => {
        const branchPayload = {
          name: b.name,

          phone_number: b.phone,
          email: b.email,
          city: b.city,
          street_address: b.address,
        };
        if (originalBranchIds.current.has(b.id)) {
          // existing branch → PATCH
          branchOps.push(
            apiService.patch(
              `api/companies/company-branch/${b.id}/`,
              branchPayload,
            ),
          );
        } else {
          // new branch → POST
          branchOps.push(
            apiService.post(`api/companies/company-branch/`, {
              ...branchPayload,
              company_id: Number(id),
            }),
          );
        }
      });

      // removed branches → DELETE
      originalBranchIds.current.forEach((origId) => {
        if (!currentBranchIds.has(origId)) {
          branchOps.push(
            apiService.delete(`api/companies/company-branch/${origId}/`),
          );
        }
      });

      const branchResults = await Promise.allSettled(branchOps);
      const branchFailures = branchResults.filter(
        (r) => r.status === "rejected",
      );
      if (branchFailures.length > 0) {
        console.error("Branch save failures:", branchFailures);
        toast.error(
          `${branchFailures.length} branch(es) failed to save. Check console for details.`,
        );
      }
      // 3. Representatives — diff by existingContactId membership
      const currentContactIds = new Set(
        formData.representatives
          .map((r) => r.existingContactId)
          .filter((v): v is number => v !== undefined),
      );

      const repOps: Promise<unknown>[] = [];

      formData.representatives.forEach((r) => {
        if (r.existingContactId) {
          // existing rep → PATCH fields (+ keep them linked to HQ branch)
          repOps.push(
            apiService.patch(`api/contacts/contact/${r.existingContactId}/`, {
              first_name: r.firstName,
              last_name: r.lastName,
              role: r.role,
              phone: r.phone,
              email: r.email,
              company_branch_id: hqBranchId.current,
            }),
          );
        } else {
          // new rep → POST, created already linked via company_branch_id
          repOps.push(
            apiService.post(`api/contacts/contact/`, {
              first_name: r.firstName,
              last_name: r.lastName,
              role: r.role,
              phone: r.phone,
              email: r.email,
              contact_type: "individual",
              company_branch_id: hqBranchId.current,
            }),
          );
        }
      });

      // removed reps → unlink (PATCH company_branch_id to null)
      originalRepContactIds.current.forEach((origContactId) => {
        if (!currentContactIds.has(origContactId)) {
          repOps.push(
            apiService.patch(`api/contacts/contact/${origContactId}/`, {
              company_branch_id: null,
            }),
          );
        }
      });

      const repResults = await Promise.allSettled(repOps);
      const repFailures = repResults.filter((r) => r.status === "rejected");
      if (repFailures.length > 0) {
        console.error("Representative save failures:", repFailures);
        toast.error(
          `${repFailures.length} representative(s) failed to save. Check console for details.`,
        );
      }

      // 4. Logo — only if a new file was picked
      if (formData.logoMode === "upload" && formData.logoFile) {
        const logoForm = new FormData();
        logoForm.append("company_logo", formData.logoFile);
        await apiService.patch(`api/companies/company/${id}/`, logoForm);
      }

      toast.success("Company updated successfully!");
      navigate(`/companies/${id}`);
    } catch (err) {
      console.error("Error updating company:", err);
      toast.error("Failed to update company. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepContent: Record<number, React.ReactNode> = {
    0: <Identity formData={formData} setFormData={setFormData} />,
    1: <Branches formData={formData} setFormData={setFormData} />,
    2: <Representatives formData={formData} setFormData={setFormData} />,
    3: <Branding formData={formData} setFormData={setFormData} />,
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AddCompanyLayout
      activeStep={activeStep}
      formData={formData}
      setFormData={setFormData}
      onStepClick={handleStepClick}
      onNext={handleNext}
      onPrevious={handlePrevious}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
    >
      {stepContent[activeStep]}
    </AddCompanyLayout>
  );
}
